import { Assets, Container, Graphics, Sprite, Texture } from 'pixi.js';
import gsap from 'gsap';
import type { ClimberPose } from '@shared/types';
import type { EquippedSet } from '@shared/protocol';
import { CLIMBER_BASE_W, CLIMBER_BASE_H, CLIMBER_SCALE } from '@shared/gameConfig';
import { NEON_COLORS } from '../colors';
import { resolveAssetUrl } from '@/shop/assetUrl';

const DEFAULT_BODY_TINT_FROM_AVATAR = {
  red: 0xffffff,
  blue: NEON_COLORS.blue.base,
} as const;

function hexStringToNumber(s: string | undefined | null, fallback: number): number {
  if (!s) return fallback;
  const m = /^#?([0-9a-f]{6})$/i.exec(s);
  if (!m) return fallback;
  return parseInt(m[1]!, 16);
}

// Character pose textures are fetched via Assets.load(url) and cached here so
// repeated equips / both lanes sharing a skin don't refetch. Kept as a hook so
// MatchScene can invalidate GPU textures on scene destroy.
const cosmeticTextureCache = new Map<string, Promise<Texture | null>>();

/**
 * Pixi v8: `Texture.from(remoteUrl)` does NOT fetch an uncached image (unlike
 * v7) — it hands back an unloaded source, so the character PNG never appears.
 * Go through `Assets.load` (same path the default climber textures use), which
 * actually downloads + GPU-uploads the image, then resolve the Texture.
 */
function loadCosmeticTexture(url: string): Promise<Texture | null> {
  const cached = cosmeticTextureCache.get(url);
  if (cached) return cached;
  const p = Assets.load<Texture>(url)
    .then((t) => t ?? null)
    .catch(() => null);
  cosmeticTextureCache.set(url, p);
  return p;
}

/**
 * Warm the texture cache for a character's pose PNGs. Called on MATCH_FOUND so
 * the (network-bound) download finishes during the countdown instead of mid-
 * match — the first time a given character appears. Fire-and-forget; failures
 * are swallowed and the climber just falls back to the default texture.
 */
export async function preloadCharacterTextureUrls(
  urls: Array<string | null | undefined>,
): Promise<void> {
  const loads: Array<Promise<Texture | null>> = [];
  for (const url of urls) {
    const resolved = resolveAssetUrl(url);
    if (resolved) loads.push(loadCosmeticTexture(resolved));
  }
  await Promise.all(loads);
}

export function preloadCharacterTextures(
  skin: import('@shared/protocol').CharacterSkin | null | undefined,
): Promise<void> {
  if (!skin) return Promise.resolve();
  return preloadCharacterTextureUrls([
    skin.idleUrl, skin.climbUrl, skin.fallUrl, skin.winUrl,
    ...(skin.idleFrames ?? []), ...(skin.climbFrames ?? []),
    ...(skin.fallFrames ?? []), ...(skin.winFrames ?? []),
  ]);
}

// Drop only the local cache; do NOT call Assets.unload (see textures.ts for
// the dev double-mount race rationale).
export function resetCosmeticTextureCache(): void {
  cosmeticTextureCache.clear();
}

// Final on-screen size for the climber sprite. Whatever the PNG's native
// dimensions are, we scale to match this so very large source art (the
// prototype PNGs are 1024×1536) doesn't dwarf the lane.
const DISPLAY_W = CLIMBER_BASE_W * CLIMBER_SCALE; // ≈31px
const DISPLAY_H = CLIMBER_BASE_H * CLIMBER_SCALE; // ≈46px

/**
 * In-match climber. Body is a single PNG sprite (idle/climb/fall/victory) that
 * we tint per body cosmetic. Hat + cape ride on top as overlay Graphics or
 * sprites. Avatar frame + victory shout are React HUD, not Pixi.
 *
 * Reverted from the procedural rig approach because that variant was too heavy
 * for mid-range phones — the rig redrew 4 limbs per frame which choked the
 * Pixi tick and prevented the climber position tween from rendering visibly.
 */
export class PixiClimber extends Container {
  private sprite: Sprite;
  private shadow: Graphics;
  /** Soft neon halo behind the body — pulses on idle, flares on each climb,
   * and brightens for rarer characters (driven by stompPower). */
  private aura: Graphics;
  private auraTween?: gsap.core.Timeline | gsap.core.Tween;
  private readonly baseGlowColor: number;
  private glowColor: number;
  private glowStrength = 1;
  private currentPose: ClimberPose = 'idle';
  private idleTween?: gsap.core.Tween;
  private oneshotTween?: gsap.core.Timeline;
  private capeTween?: gsap.core.Tween;
  private readonly mirror: boolean;
  private readonly color: 'red' | 'blue';
  private tintColor: number;
  private baseScaleX = CLIMBER_SCALE;
  private baseScaleY = CLIMBER_SCALE;
  private equipped: EquippedSet | null = null;

  constructor(opts: { textures: Record<ClimberPose, Texture>; mirror?: boolean; color: 'red' | 'blue' }) {
    super();
    this.mirror = opts.mirror ?? false;
    this.color = opts.color;
    this.tintColor = DEFAULT_BODY_TINT_FROM_AVATAR[this.color];
    this.baseGlowColor = NEON_COLORS[this.color].base;
    this.glowColor = this.baseGlowColor;

    this.shadow = new Graphics();
    this.shadow.ellipse(0, DISPLAY_H * 0.5 - 2, DISPLAY_W * 0.55, 5).fill({
      color: 0x000000,
      alpha: 0.35,
    });
    this.addChild(this.shadow);

    // Halo sits BEHIND the body (added before the sprite) and uses additive
    // blend so overlapping layers read as a bright neon bloom.
    this.aura = new Graphics();
    this.aura.blendMode = 'add';
    this.aura.y = -DISPLAY_H * 0.5;
    this.addChild(this.aura);
    this.drawAura();

    this.sprite = new Sprite(opts.textures.idle);
    this.sprite.anchor.set(0.5, 1);
    this.applyTextureScale(opts.textures.idle);
    this.sprite.tint = this.tintColor;
    this.addChild(this.sprite);

    this.startIdle();
    this.startAuraPulse();
  }

  /**
   * Compute the scale needed to make this texture render at DISPLAY_W × DISPLAY_H.
   * Run on every pose swap because different PNGs may have slightly different
   * native sizes (e.g. fall art with different padding).
   */
  private applyTextureScale(texture: Texture): void {
    const w = texture.width || CLIMBER_BASE_W;
    const h = texture.height || CLIMBER_BASE_H;
    const s = Math.min(DISPLAY_W / w, DISPLAY_H / h);
    this.baseScaleX = this.mirror ? -s : s;
    this.baseScaleY = s;
    this.sprite.scale.set(this.baseScaleX, this.baseScaleY);
  }

  setTextures(textures: Record<ClimberPose, Texture>): void {
    this.sprite.texture = this.textureFor(this.currentPose, textures);
    this.applyTextureScale(this.sprite.texture);
  }

  /** Character override per pose. `idle`/`climb`/`fall`/`win`→`victory` map to
   * the uploaded character PNGs; a missing pose falls back to the default
   * climber texture so a half-authored character still renders. */
  private charTextures: Partial<Record<ClimberPose, Texture>> = {};
  /** Ordered animation frames per pose (when a pose has >1 frame). Played as a
   * flipbook while that pose is active; charTextures holds frame 0 as the
   * static fallback. */
  private charFrames: Partial<Record<ClimberPose, Texture[]>> = {};
  private flipTween?: gsap.core.Timeline;
  private charTint: number | null = null;
  /** Bumped on every setCharacterSkin so async loads from a stale skin (or
   * after teardown) are discarded instead of clobbering the current sprite. */
  private charGen = 0;

  setCharacterSkin(skin: import('@shared/protocol').CharacterSkin | null): void {
    const gen = ++this.charGen;
    // Tear down any previous character textures + running flipbook.
    this.charTextures = {};
    this.charFrames = {};
    this.flipTween?.kill();
    this.flipTween = undefined;
    this.charTint = null;
    if (!skin) {
      if (!this.sprite.destroyed) this.sprite.tint = this.tintColor;
      this.glowStrength = 1;
      this.glowColor = this.baseGlowColor;
      this.drawAura();
      this.startAuraPulse();
      return;
    }
    if (skin.tint) {
      this.charTint = hexStringToNumber(skin.tint, this.tintColor);
      this.glowColor = this.charTint;
    } else {
      this.glowColor = this.baseGlowColor;
    }
    // Tint whatever's on screen now (still the default climber). Once a pose's
    // PNG loads, applyTintFor switches it to white so the art's own colors
    // render untouched instead of being multiplied/hidden by the tint.
    this.applyTintFor(this.currentPose);
    // Rarer characters carry a higher stompPower → stronger, wider halo.
    this.glowStrength = Math.max(0.8, Math.min(1.8, skin.stompPower || 1));
    this.drawAura();
    this.startAuraPulse();
    // Load a pose: `frames` is the full ordered list ([base, ...extra]) when the
    // character ships an animation; otherwise just the single base url. Frame 0
    // becomes the static charTextures fallback; ≥2 frames arm the flipbook.
    const load = (base: string | null, frames: string[] | null | undefined, pose: ClimberPose) => {
      const list = (frames && frames.length ? frames : base ? [base] : [])
        .map((u) => resolveAssetUrl(u))
        .filter((u): u is string => !!u);
      if (!list.length) return;
      void Promise.all(list.map(loadCosmeticTexture)).then((texes) => {
        // Skin changed (or climber torn down) mid-load — drop this result.
        if (gen !== this.charGen || this.sprite.destroyed) return;
        const valid = texes.filter((t): t is Texture => !!t);
        if (!valid.length) return;
        this.charTextures[pose] = valid[0]!;
        if (valid.length > 1) this.charFrames[pose] = valid;
        // If this is the pose we're currently showing, swap + (re)arm flipbook.
        if (this.currentPose === pose) {
          this.sprite.texture = valid[0]!;
          this.applyTextureScale(valid[0]!);
          this.applyTintFor(pose);
          this.startFlipbook(pose);
        }
      });
    };
    load(skin.idleUrl, skin.idleFrames, 'idle');
    load(skin.climbUrl, skin.climbFrames, 'climb');
    load(skin.fallUrl, skin.fallFrames, 'fall');
    load(skin.winUrl, skin.winFrames, 'victory');
  }

  /** Cycle the per-pose frame array as a flipbook while `pose` is active.
   * Looping poses (idle/victory) run slow; transient action poses (climb/fall)
   * run faster for a snappier read. A pose with <2 frames just kills the tween
   * and leaves the static texture in place. */
  private startFlipbook(pose: ClimberPose): void {
    this.flipTween?.kill();
    this.flipTween = undefined;
    const frames = this.charFrames[pose];
    if (!frames || frames.length < 2 || this.sprite.destroyed) return;
    const fps = pose === 'climb' || pose === 'fall' ? 14 : 6;
    const step = { i: 0 };
    this.flipTween = gsap.timeline({ repeat: -1 }).to(step, {
      i: frames.length,
      duration: frames.length / fps,
      ease: 'none',
      modifiers: { i: (v: number) => Math.floor(Number(v)) % frames.length },
      onUpdate: () => {
        if (this.sprite.destroyed) return;
        const tex = frames[step.i % frames.length]!;
        if (this.sprite.texture === tex) return;
        const prev = this.sprite.texture;
        this.sprite.texture = tex;
        // Frames in one pose normally share dimensions; only re-fit (which
        // resets sprite.scale) when they don't, so we never fight an in-flight
        // squash/stretch tween (victory stomp, climb hop).
        if (tex.width !== prev.width || tex.height !== prev.height) {
          this.applyTextureScale(tex);
        }
      },
    });
  }

  private textureFor(pose: ClimberPose, fallback: Record<ClimberPose, Texture>): Texture {
    return this.charTextures[pose] ?? fallback[pose];
  }

  /** A full-color character PNG must render with NO tint (white) or `sprite.tint`
   * multiplies its colors and hides the art. Only a pose falling back to the
   * default (monochrome-ish) climber gets the themed/lane tint. */
  private applyTintFor(pose: ClimberPose): void {
    if (this.sprite.destroyed) return;
    this.sprite.tint = this.charTextures[pose] ? 0xffffff : (this.charTint ?? this.tintColor);
  }

  setPose(pose: ClimberPose, textures: Record<ClimberPose, Texture>): void {
    if (this.currentPose === pose) return;
    this.currentPose = pose;
    const tex = this.textureFor(pose, textures);
    this.sprite.texture = tex;
    this.applyTextureScale(tex);
    this.applyTintFor(pose);

    this.oneshotTween?.kill();
    this.oneshotTween = undefined;
    // Arm/kill the flipbook for the new pose (no-op when <2 frames).
    this.startFlipbook(pose);

    switch (pose) {
      case 'idle':
        this.startIdle();
        break;
      case 'climb':
        this.stopIdle();
        this.playClimbHop();
        break;
      case 'fall':
        this.stopIdle();
        this.playFallWobble();
        break;
      case 'victory':
        this.stopIdle();
        this.playVictoryCheer();
        break;
    }
  }

  private startIdle(): void {
    this.idleTween?.kill();
    if (this.sprite.destroyed) return;
    this.sprite.y = 0;
    this.sprite.rotation = 0;
    this.sprite.scale.set(this.baseScaleX, this.baseScaleY);
    this.idleTween = gsap.to(this.sprite, {
      y: -4,
      duration: 0.9,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
    });
  }

  private stopIdle(): void {
    this.idleTween?.kill();
    this.idleTween = undefined;
  }

  // ── neon halo ──────────────────────────────────────────────────────

  /** Two concentric soft discs that, under additive blend, fake a radial
   * neon glow without a (GPU-heavy) blur filter — safe for mid-range phones. */
  private drawAura(): void {
    if (this.aura.destroyed) return;
    const r = DISPLAY_W * (0.85 + 0.25 * (this.glowStrength - 1));
    this.aura
      .clear()
      .circle(0, 0, r)
      .fill({ color: this.glowColor, alpha: 0.14 })
      .circle(0, 0, r * 0.62)
      .fill({ color: this.glowColor, alpha: 0.2 });
  }

  /** Gentle breathing pulse — the default resting state of the halo. */
  private startAuraPulse(): void {
    this.auraTween?.kill();
    if (this.aura.destroyed) return;
    const s = 0.9 * this.glowStrength;
    this.aura.scale.set(s);
    this.aura.alpha = 0.6;
    const tl = gsap.timeline({ repeat: -1, yoyo: true });
    tl.to(this.aura, { alpha: 0.95, duration: 1.1, ease: 'sine.inOut' }, 0);
    tl.to(this.aura.scale, { x: s * 1.12, y: s * 1.12, duration: 1.1, ease: 'sine.inOut' }, 0);
    this.auraTween = tl;
  }

  /** Quick bloom on each successful climb — pops bright + wide, then settles
   * back into the resting pulse. */
  private flareAura(): void {
    if (this.aura.destroyed) return;
    this.auraTween?.kill();
    const s = 0.9 * this.glowStrength;
    const tl = gsap.timeline({ onComplete: () => this.startAuraPulse() });
    tl.fromTo(this.aura, { alpha: 1 }, { alpha: 0.7, duration: 0.32, ease: 'power2.out' }, 0);
    tl.fromTo(
      this.aura.scale,
      { x: s * 1.5, y: s * 1.5 },
      { x: s, y: s, duration: 0.32, ease: 'power2.out' },
      0,
    );
    this.auraTween = tl;
  }

  private playClimbHop(): void {
    this.flareAura();
    // Pixi Sprite has NO `scaleX`/`scaleY` properties — only `sprite.scale.x`.
    // GSAP can't tween those directly and used to bail with "Invalid property"
    // which silently broke the climb animation. y + rotation alone is enough
    // visual feedback for the hop.
    const tl = gsap.timeline({
      onComplete: () => {
        if (this.sprite.destroyed) return;
        this.sprite.y = 0;
        this.sprite.rotation = 0;
      },
    });
    tl.to(this.sprite, {
      y: -5,
      rotation: 0.06,
      duration: 0.18,
      ease: 'sine.out',
    });
    tl.to(this.sprite, {
      y: 0,
      rotation: 0,
      duration: 0.22,
      ease: 'sine.inOut',
    });
    this.oneshotTween = tl;
  }

  private playFallWobble(): void {
    const tl = gsap.timeline({
      onComplete: () => {
        if (this.sprite.destroyed) return;
        this.sprite.y = 0;
        this.sprite.rotation = 0;
      },
    });
    tl.set(this.sprite, { y: -14 });
    tl.to(this.sprite, { rotation: -0.31, duration: 0.18, ease: 'power2.out' });
    tl.to(this.sprite, { rotation: 0.38, y: 12, duration: 0.22 });
    tl.to(this.sprite, { rotation: -0.17, y: 4, duration: 0.16 });
    tl.to(this.sprite, { rotation: 0, y: 0, duration: 0.14 });
    this.oneshotTween = tl;
  }

  private playVictoryCheer(): void {
    this.idleTween?.kill();
    // y bob only — see playClimbHop for why we skip scale.
    this.idleTween = gsap.to(this.sprite, {
      y: -6,
      duration: 0.6,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
    });
  }

  // ── win/lose dramatics ───────────────────────────────────────────

  /** Winner's victory STOMP — rear up, then slam straight down with a heavy
   * squash. `onImpact` fires at the slam frame so the scene can spawn dust +
   * shockwave + screen shake in sync. */
  playWinStomp(onImpact?: () => void): void {
    this.stopIdle();
    this.oneshotTween?.kill();
    if (this.sprite.destroyed) return;
    const baseY = 0;
    const tl = gsap.timeline();
    // Rear up high with a slight back-lean + grow.
    tl.to(this.sprite, { y: -38, rotation: -0.25, duration: 0.22, ease: 'power2.out' });
    tl.to(this.sprite.scale, { x: this.baseScaleX * 1.25, y: this.baseScaleY * 1.25, duration: 0.22, ease: 'power2.out' }, '<');
    // SLAM down — fast, overshoot into a squash.
    tl.to(this.sprite, {
      y: baseY,
      rotation: 0,
      duration: 0.1,
      ease: 'power4.in',
      onComplete: () => onImpact?.(),
    });
    tl.to(this.sprite.scale, { x: this.baseScaleX * 1.4, y: this.baseScaleY * 0.7, duration: 0.06, ease: 'power2.out' }, '<');
    // Recoil back to a proud victory pose, then loop the cheer bob.
    tl.to(this.sprite.scale, { x: this.baseScaleX, y: this.baseScaleY, duration: 0.5, ease: 'elastic.out(1.3, 0.4)' });
    tl.add(() => { if (!this.sprite.destroyed) this.playVictoryCheer(); });
    this.oneshotTween = tl;
  }

  /** Loser gets STOMPED — violently knocked down: spin, tumble off the wall,
   * fade out as they plummet. The most dramatic "defeat" beat. */
  playKnockedDown(distance = 220): void {
    this.stopIdle();
    this.oneshotTween?.kill();
    if (this.sprite.destroyed) return;
    const tl = gsap.timeline();
    // Sharp recoil up from the hit.
    tl.to(this.sprite, { y: -16, rotation: 0.4, duration: 0.08, ease: 'power3.out' });
    // Then tumble down the wall, spinning, shrinking + fading.
    tl.to(this.sprite, {
      y: distance,
      rotation: 3.4,
      duration: 0.7,
      ease: 'power2.in',
    });
    tl.to(this.sprite.scale, { x: this.baseScaleX * 0.55, y: this.baseScaleY * 0.55, duration: 0.7, ease: 'power1.in' }, '<');
    tl.to(this.sprite, { alpha: 0, duration: 0.3, ease: 'power1.in' }, '-=0.25');
    this.oneshotTween = tl;
  }

  // ── cosmetics ────────────────────────────────────────────────────
  // hat/outfit/cape were removed when the game switched to full Characters.
  // The only "cosmetic" the climber renders now is the equipped Character's
  // pose sprites (see setCharacterSkin above). setEquipped is kept as a
  // no-op so the old call sites don't break.

  setEquipped(equipped: EquippedSet | null): void {
    this.equipped = equipped;
  }

  getEquipped(): EquippedSet | null {
    return this.equipped;
  }

  destroyClimber(): void {
    this.idleTween?.kill();
    this.oneshotTween?.kill();
    this.capeTween?.kill();
    this.auraTween?.kill();
    this.flipTween?.kill();
    gsap.killTweensOf(this.sprite);
    gsap.killTweensOf(this.aura);
    gsap.killTweensOf(this.aura.scale);
    this.destroy({ children: true });
  }

  static getBaseSize(): { w: number; h: number } {
    return { w: DISPLAY_W, h: DISPLAY_H };
  }
}
